import type { ReactNode } from 'react';
import { type FileUploadItem, isFileUploadItemWeb } from '@gov-nx/core/hooks';
import { Button, CommaSpace } from '@gov-nx/ui/web';
import { isLastIndex } from '@gov-nx/utils/common';
import { downloadAttachment, openAttachment } from '@gov-nx/utils/web';

interface RecapItem {
	label: string;
	value?: string | FileUploadItem[];
}

interface RecapOption extends RecapItem {
	children?: RecapItem[];
}

interface Props {
	emptyMessage?: string;
	options: RecapOption[];
	downloadAttachments?: boolean;
}

interface RecapValueProps {
	value: string | FileUploadItem[] | undefined;
	emptyMessage: string;
	downloadAttachments: boolean;
}

const RecapValue = ({
	value,
	emptyMessage,
	downloadAttachments,
}: RecapValueProps): ReactNode => {
	if (Array.isArray(value)) {
		if (value.length === 0) {
			return emptyMessage;
		}

		const webAttachments = value.filter(isFileUploadItemWeb);

		if (webAttachments.length > 0) {
			return (
				<span className="inline-flex flex-wrap gap-x-2">
					{webAttachments.map((attachment, index) => (
						<span
							key={attachment.id}
							className="inline-flex items-center">
							<Button
								color="primary"
								type="link"
								className="wrap-normal"
								onClick={() =>
									downloadAttachments
										? downloadAttachment(attachment)
										: openAttachment(attachment)
								}
								aria-label={attachment.name}>
								{attachment.name}
							</Button>
							{!isLastIndex(index, webAttachments) ? <CommaSpace /> : null}
						</span>
					))}
				</span>
			);
		}

		return emptyMessage;
	}

	return value ?? emptyMessage;
};

export const WizardRecapKeyValues = ({
	options,
	emptyMessage = '-',
	downloadAttachments = false,
}: Props) => (
	<dl className="flex flex-col gap-2">
		{options.map(({ value, label, children }) => (
			<div key={label}>
				<dt className="inline mr-1! font-bold">{label}</dt>
				{children && children.length > 0 ? (
					<dl className="mt-2 pl-6 flex flex-col gap-2">
						{children.map((child) => (
							<div key={child.label}>
								<dt className="inline mr-1! font-bold">{child.label}</dt>
								<dd className="inline">
									<RecapValue
										value={child.value}
										emptyMessage={emptyMessage}
										downloadAttachments={downloadAttachments}
									/>
								</dd>
							</div>
						))}
					</dl>
				) : (
					<dd className="inline">
						<RecapValue
							value={value}
							emptyMessage={emptyMessage}
							downloadAttachments={downloadAttachments}
						/>
					</dd>
				)}
			</div>
		))}
	</dl>
);
